import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useApp } from "../context/AppContext";
import { PRODUCTS, IMAGES } from "../mock";
import { getProducts } from "../lib/api";
import ProductCard from "./ProductCard";

const normalizeDynamic = (d) => ({
  id: d.product_id,
  index: "",
  tag: d.tag || "NEW",
  preorder: !!d.preorder,
  image: d.image || IMAGES.lipGloss,
  price: d.price,
  volume: "",
  category: { nl: d.category || "Nieuw", en: d.category || "New" },
  name: { nl: d.name, en: d.name },
  subtitle: { nl: d.subtitle || "", en: d.subtitle || "" },
  short: { nl: d.description || "", en: d.description || "" },
});

export const Hero = () => {
  const { t } = useApp();
  const scrollToCollection = () =>
    document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative overflow-hidden">
      <div className="max-w-[1320px] mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-2 gap-16 items-center">
        <div className="rise-in">
          <div className="flex items-center gap-3 mb-8">
            <span className="text-[11px] tracking-label uppercase text-[#A08678]">{t.heroKicker}</span>
            <span className="h-px w-8 bg-[#D9C3B7]" />
            <span className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-[#C79A7E] text-[#C79A7E]" />
              ))}
              <span className="text-[11px] text-[#A08678] ml-1">4.9</span>
            </span>
          </div>
          <h1 className="font-serif font-light text-[56px] leading-[0.95] md:text-[84px] text-[#3A2C28] tracking-[-0.01em]">
            <span className="block">{t.heroTitle1}</span>
            <span className="block italic text-[#B4715C]">{t.heroTitle2}</span>
            <span className="block">{t.heroTitle3}</span>
          </h1>
          <p className="mt-8 max-w-md text-[15px] leading-[1.75] text-[#6E5A52]">{t.heroText}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={scrollToCollection}
              className="h-12 px-9 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]"
            >
              {t.heroCta1}
            </button>
            <a href="/about" className="text-[11px] tracking-label uppercase text-[#5A4740] link-underline hover:text-[#B4715C]">
              {t.heroCta2}
            </a>
          </div>
          <div className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-label uppercase text-[#A08678]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8FB89A] animate-pulse" />
            {t.live}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 -z-10 bg-gradient-to-br from-[#F4E5DC] to-[#EAD3C6] rounded-[3rem] blur-3xl opacity-60" />
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-b from-[#FBF2EC] to-[#F1DFD4] border border-[#EEE0D7] shadow-[0_40px_80px_-40px_rgba(150,100,80,0.4)] floaty">
            <img src={IMAGES.serum} alt="Copper Peptide Complex" className="w-full h-[440px] object-contain p-10" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="rounded-2xl overflow-hidden bg-white border border-[#EEE0D7]">
              <img src={IMAGES.lipTints} alt="Lip tints" className="w-full h-32 object-cover" />
            </div>
            <div className="rounded-2xl bg-white border border-[#EEE0D7] flex flex-col items-center justify-center py-6">
              <p className="font-serif text-2xl text-[#3A2C28] leading-none">GHK-Cu</p>
              <p className="text-[10px] tracking-label uppercase mt-2 text-[#A08678]">Copper Peptide</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#EAD9CE]">
        <div className="max-w-[1320px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#EAD9CE]">
          {[
            { v: "100%", l: t.stat1 },
            { v: "24h", l: t.stat2 },
            { v: "2.000+", l: t.stat3 },
            { v: "4.9/5", l: "reviews" },
          ].map((s, i) => (
            <div key={i} className="py-8 px-4 text-center">
              <p className="font-serif text-3xl text-[#3A2C28]">{s.v}</p>
              <p className="text-[11px] tracking-label uppercase text-[#A08678] mt-1.5">{s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Catalogue = () => {
  const { t } = useApp();
  const [dynamic, setDynamic] = useState([]);
  useEffect(() => {
    let active = true;
    getProducts()
      .then((res) => active && setDynamic((res.dynamic || []).map(normalizeDynamic)))
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const all = [...PRODUCTS, ...dynamic];
  return (
    <section id="collection" className="relative py-24 md:py-32">
      <div className="max-w-[1320px] mx-auto px-6">
        <span className="text-[11px] tracking-label uppercase text-[#A08678]">{t.catKicker}</span>
        <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <h2 className="font-serif font-light text-4xl md:text-6xl text-[#3A2C28] max-w-xl leading-[1.05] tracking-[-0.01em]">
            {t.catTitle}
          </h2>
          <p className="text-sm leading-relaxed text-[#6E5A52] max-w-xs">{t.catText}</p>
        </div>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {all.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 90} />
          ))}
        </div>
      </div>
    </section>
  );
};
